'use client';

import { ErrorResponse, Log, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import React, { useEffect, useRef } from 'react';
import { AuthProvider as OIDCAuthProvider } from 'react-oidc-context';

const isOktaProvider = process.env.AUTH_CLIENT_PROVIDER === 'okta';

type OAuthProviderContextType = {
  manager: UserManager | null;
};

const OAuthProviderContext = React.createContext<OAuthProviderContextType>(
  {
    manager: null,
  },
);

function createUserManager(): UserManager {
  const settings: UserManagerSettings = {
    authority: process.env.AUTH_CLIENT_ISSUER || `https://${process.env.AUTH_CLIENT_DOMAIN}`,
    client_id: process.env.AUTH_CLIENT_ID as string,
    redirect_uri: `${window.location.origin}/callback`,
    post_logout_redirect_uri: `${window.location.origin}/logout`,
    scope: process.env.AUTH_CLIENT_SCOPE,
    // For Gov environments (they use OKTA), we use session storage
    userStore: new WebStorageStateStore({ store: isOktaProvider ? window.sessionStorage : window.localStorage }),
    loadUserInfo: true,
    automaticSilentRenew: true,
    response_type: 'code',
    extraQueryParams: { audience: process.env.AUTH_CLIENT_AUDIENCE_BROWSER as string },
  };

  return new UserManager(settings);
}

export const OAuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const managerRef = useRef<UserManager | null>(null);

  if (typeof window !== 'undefined' && !managerRef.current) {
    managerRef.current = createUserManager();
  }

  const context: OAuthProviderContextType = React.useMemo(() => ({
    manager: managerRef.current,
  }), []);

  useEffect(() => {
    const manager = managerRef.current;

    if (!manager) {
      return;
    }

    Log.setLogger(console);
    Log.setLevel(Log.WARN);

    const handleSilentRenewError = (error: Error) => {
      const oauthError = error instanceof ErrorResponse ? error.error : error.message;
      const isSessionGone = oauthError === 'login_required' ||
        oauthError === 'consent_required' ||
        oauthError === 'interaction_required';

      if (isSessionGone) {
        manager.stopSilentRenew();
        manager.removeUser();
      }
    };

    manager.events.addSilentRenewError(handleSilentRenewError);

    return () => {
      manager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, []);

  if (!managerRef.current) {
    return null;
  }

  return (
    <OIDCAuthProvider userManager={managerRef.current} skipSigninCallback>
      <OAuthProviderContext.Provider value={context}>{children}</OAuthProviderContext.Provider>
    </OIDCAuthProvider>
  );
};

export const useOAuthProvider = (): OAuthProviderContextType => React.useContext(OAuthProviderContext);
